/**
 * Backfill Clean Week Recognitions
 *
 * Awards recognitions to students who had NO mentions during a week.
 * Processes the last 3 completed weeks (excludes current incomplete week).
 *
 * USAGE:
 *   await window.backfillCleanWeekRecognitions()                    // Dry run (preview)
 *   await window.backfillCleanWeekRecognitions({ execute: true })   // Create recognitions
 *
 * OPTIONS:
 *   execute: false (default) - Preview mode, shows what would be created
 *   execute: true - Actually create the recognitions
 *   weeksBack: 3 (default) - Number of completed weeks to process
 */

import { db, auth } from '@/firebase';
import {
  collection,
  getDocs,
  addDoc,
  query,
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

/**
 * Get school week boundaries (Monday to Friday)
 */
function getWeekBoundaries(referenceDate) {
  const date = new Date(referenceDate);
  const day = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const daysFromMonday = day === 0 ? 6 : day - 1;

  // Start of week (Monday 00:00:00)
  const start = new Date(date);
  start.setDate(date.getDate() - daysFromMonday);
  start.setHours(0, 0, 0, 0);

  // End of week (Friday 23:59:59.999) - school week only
  const end = new Date(start);
  end.setDate(start.getDate() + 4); // Monday + 4 = Friday
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

/**
 * Get the last N completed weeks (excluding current incomplete week)
 */
function getLastNCompletedWeeks(count) {
  const today = new Date();
  const currentWeek = getWeekBoundaries(today);

  const weeks = [];
  for (let i = 1; i <= count; i++) {
    const referenceDate = new Date(currentWeek.start);
    referenceDate.setDate(referenceDate.getDate() - (i * 7));

    const weekBounds = getWeekBoundaries(referenceDate);
    weeks.push({
      weekNumber: i,
      start: weekBounds.start,
      end: weekBounds.end,
      label: `${weekBounds.start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${weekBounds.end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
    });
  }

  return weeks.reverse(); // Chronological order (oldest first)
}

/**
 * Get the Discipline value from the values collection
 */
async function getDisciplineValue(schoolId) {
  const valuesRef = collection(db, 'schools', schoolId, 'values');
  const q = query(valuesRef, where('active', '==', true));
  const snapshot = await getDocs(q);

  let disciplineValue = null;
  snapshot.forEach(doc => {
    const data = doc.data();
    if (data.name?.toLowerCase() === 'discipline') {
      disciplineValue = { id: doc.id, ...data };
    }
  });

  return disciplineValue;
}

/**
 * Get all active, non-graduated students for a school
 */
async function getActiveStudents(schoolId) {
  const studentsRef = collection(db, 'schools', schoolId, 'students');
  const snapshot = await getDocs(studentsRef);

  const students = [];
  snapshot.forEach(doc => {
    const data = doc.data();
    // Exclude past students - check status field AND boolean flags
    const isPastStudent =
      data.status === 'graduated' ||
      data.status === 'inactive' ||
      data.graduated === true ||
      data.active === false;

    if (!isPastStudent) {
      students.push({ id: doc.id, ...data });
    }
  });

  return students;
}

/**
 * Get all mentions for a date range
 */
async function getMentionsForDateRange(schoolId, startDate, endDate) {
  const mentionsRef = collection(db, 'schools', schoolId, 'mentions');
  const snapshot = await getDocs(mentionsRef);

  const startTime = startDate.getTime();
  const endTime = endDate.getTime();

  const mentions = [];
  snapshot.forEach(doc => {
    const data = doc.data();
    const dateField = data.issuedAt || data.createdAt;
    const time = dateField?.toMillis?.() || new Date(dateField).getTime();

    if (time >= startTime && time <= endTime) {
      mentions.push({ id: doc.id, ...data });
    }
  });

  return mentions;
}

/**
 * Check if a Clean Week recognition already exists for student + week
 */
async function hasExistingCleanWeekRecognition(schoolId, studentId, weekStart, weekEnd) {
  const recognitionsRef = collection(db, 'schools', schoolId, 'recognitions');
  const snapshot = await getDocs(recognitionsRef);

  const startTime = weekStart.getTime();
  const endTime = weekEnd.getTime();

  let found = false;
  snapshot.forEach(doc => {
    const data = doc.data();
    if (data.studentId === studentId && data.reason?.includes('Clean Week')) {
      const dateField = data.issuedAt || data.createdAt;
      const time = dateField?.toMillis?.() || new Date(dateField).getTime();
      if (time >= startTime && time <= endTime) {
        found = true;
      }
    }
  });

  return found;
}

/**
 * Main backfill function
 */
async function backfillCleanWeekRecognitions(options = {}) {
  const { execute = false, weeksBack = 3 } = options;

  console.log('='.repeat(60));
  console.log('CLEAN WEEK RECOGNITION BACKFILL');
  console.log(execute ? '*** EXECUTE MODE ***' : '*** PREVIEW MODE ***');
  console.log('='.repeat(60));

  const user = auth.currentUser;
  if (!user) {
    console.error('Not logged in.');
    return { success: false, error: 'Not authenticated' };
  }

  const schoolId = localStorage.getItem('currentSchoolId');
  if (!schoolId) {
    console.error('No school selected.');
    return { success: false, error: 'No school selected' };
  }

  console.log(`School: ${schoolId}`);
  console.log(`User: ${user.displayName || user.email}\n`);

  // Get Discipline value
  console.log('Fetching "Discipline" value...');
  const disciplineValue = await getDisciplineValue(schoolId);
  if (!disciplineValue) {
    console.error('"Discipline" value not found in values collection.');
    return { success: false, error: 'Discipline value not found' };
  }
  console.log(`Found: ${disciplineValue.name} (${disciplineValue.id})\n`);

  // Get students
  console.log('Fetching active students...');
  const students = await getActiveStudents(schoolId);
  console.log(`Found ${students.length} active students\n`);

  // Get weeks
  const weeks = getLastNCompletedWeeks(weeksBack);
  console.log(`Processing ${weeks.length} completed weeks:`);
  weeks.forEach(w => console.log(`  - ${w.label}`));
  console.log('');

  const results = {
    weeks: [],
    totalToCreate: 0,
    totalCreated: 0,
    totalSkipped: 0,
    errors: []
  };

  for (const week of weeks) {
    console.log('-'.repeat(50));
    console.log(`Week: ${week.label}`);

    const mentions = await getMentionsForDateRange(schoolId, week.start, week.end);

    // Students with mentions this week
    const studentIdsWithMentions = new Set(mentions.map(m => m.studentId));

    // Students WITHOUT mentions = clean week
    const cleanWeekStudents = students.filter(s => !studentIdsWithMentions.has(s.id));

    console.log(`  Total mentions: ${mentions.length}`);
    console.log(`  Students with mentions: ${studentIdsWithMentions.size}`);
    console.log(`  Students with CLEAN week: ${cleanWeekStudents.length}`);

    const weekResult = {
      week: week.label,
      mentions: mentions.length,
      withMentions: studentIdsWithMentions.size,
      cleanWeek: cleanWeekStudents.length,
      created: 0,
      skipped: 0,
      students: []
    };

    for (const student of cleanWeekStudents) {
      // Handle missing lastName - use surname as fallback, or just firstName
      const lastName = student.lastName || student.surname || '';
      const studentName = lastName ? `${student.firstName} ${lastName}` : student.firstName;

      // Skip if already exists
      const exists = await hasExistingCleanWeekRecognition(schoolId, student.id, week.start, week.end);
      if (exists) {
        weekResult.skipped++;
        results.totalSkipped++;
        continue;
      }

      weekResult.students.push({
        id: student.id,
        name: studentName,
        class: student.className || '-'
      });
      results.totalToCreate++;

      if (execute) {
        try {
          const recognition = {
            studentId: student.id,
            studentName,
            classId: student.classId || null,
            className: student.className || null,
            houseId: student.houseId || null,
            houseName: student.houseName || null,
            value: disciplineValue.name,
            valueId: disciplineValue.id,
            reason: `Clean Week: ${week.label}`,
            points: 1,
            issuedById: user.uid,
            issuedByName: user.displayName || 'System',
            issuedAt: Timestamp.fromDate(week.end),
            schoolId,
            type: 'recognition',
            active: true,
            processedForRules: false,
            triggeredBadges: [],
            triggeredCertificates: [],
            parentNotified: false,
            parentNotifiedAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            isBackfill: true,
            backfillDate: serverTimestamp(),
            weekStart: Timestamp.fromDate(week.start),
            weekEnd: Timestamp.fromDate(week.end)
          };

          const ref = collection(db, 'schools', schoolId, 'recognitions');
          await addDoc(ref, recognition);
          weekResult.created++;
          results.totalCreated++;
        } catch (error) {
          console.error(`  Error for ${studentName}:`, error.message);
          results.errors.push({ student: studentName, week: week.label, error: error.message });
        }
      }
    }

    if (weekResult.skipped > 0) {
      console.log(`  Skipped (already exist): ${weekResult.skipped}`);
    }
    if (execute) {
      console.log(`  Created: ${weekResult.created}`);
    }

    results.weeks.push(weekResult);
  }

  // Summary
  console.log('\n' + '='.repeat(60));
  console.log('SUMMARY');
  console.log('='.repeat(60));
  console.log(`Recognitions to create: ${results.totalToCreate}`);
  console.log(`Already existed (skipped): ${results.totalSkipped}`);

  if (execute) {
    console.log(`Created: ${results.totalCreated}`);
    if (results.errors.length > 0) {
      console.log(`Errors: ${results.errors.length}`);
    }
    console.log('\nDone!');
  } else {
    console.log('\n*** PREVIEW ONLY - No changes made ***');
    console.log('To create recognitions, run:');
    console.log('  await window.backfillCleanWeekRecognitions({ execute: true })');

    if (results.totalToCreate > 0 && results.totalToCreate <= 50) {
      console.log('\nStudents to receive Clean Week recognition:');
      results.weeks.forEach(w => {
        if (w.students.length > 0) {
          console.log(`\n${w.week}:`);
          w.students.forEach(s => console.log(`  ${s.name} (${s.class})`));
        }
      });
    }
  }

  return results;
}

export { backfillCleanWeekRecognitions };
